import React from 'react';
import { Popover } from 'app/components/common';
import { Currency, formatMoney } from '../../../common/format';
import { CoverageCostsBreakdown } from '../types';
import { ProjectionColumn, renderProjectionCell } from './projection-columns';

type RowKeyable = Record<string, number | undefined>;

interface ProjectionTableProps<TRow extends RowKeyable> {
  rows: TRow[];
  columns: ProjectionColumn<TRow>[];
  currency: Currency;
  ufValue: number;
  title?: string;
  maxHeight?: number;
  rowKey?: keyof TRow & string;
}

const COVERAGE_COST_LABELS: Record<string, string> = {
  death: 'Muerte',
  accidental_death: 'Muerte accidental',
  itp: 'ITP',
  serious_illness: 'Enfermedades graves',
};

const COVERAGE_COST_ORDER = Object.keys(COVERAGE_COST_LABELS);

function getCoverageCostLabel(key: string): string {
  return COVERAGE_COST_LABELS[key] ?? key.replace(/_/g, ' ');
}

function getOrderedCoverageCostEntries(breakdown: CoverageCostsBreakdown): [string, number][] {
  const orderedKeys = [
    ...COVERAGE_COST_ORDER.filter((key) => key in breakdown),
    ...Object.keys(breakdown).filter((key) => !COVERAGE_COST_ORDER.includes(key)),
  ];
  return orderedKeys.map((key) => [key, breakdown[key]]);
}

function renderCoverageCostsCell(
  formattedValue: string,
  breakdown: CoverageCostsBreakdown | undefined,
  popoverId: string,
  currency: Currency,
  ufValue: number,
) {
  const entries = getOrderedCoverageCostEntries(breakdown ?? {});
  if (entries.length === 0) return formattedValue;

  return (
    <Popover
      id={popoverId}
      trigger={['hover', 'focus']}
      placement="auto"
      width={220}
      renderTrigger={() => (
        <span style={{ textDecoration: 'underline dotted', cursor: 'help' }}>
          {formattedValue}
        </span>
      )}
      renderContent={() => (
        <ul className="list-unstyled text-white mb-0 p-3 d-flex flex-column gap-2">
          {entries.map(([key, value]) => (
            <li key={key} className="d-flex justify-content-between gap-3">
              <span>{getCoverageCostLabel(key)}</span>
              <span>{formatMoney(value, currency, ufValue)}</span>
            </li>
          ))}
        </ul>
      )}
    />
  );
}

export function ProjectionTable<TRow extends RowKeyable>({
  rows,
  columns,
  currency,
  ufValue,
  title = 'Detalle anual',
  maxHeight = 480,
  rowKey = 'year',
}: ProjectionTableProps<TRow>) {
  return (
    <div className="card">
      <div className="card-header">
        <strong>{title}</strong>
      </div>
      <div className="table-responsive" style={{ maxHeight }}>
        <table className="table table-sm table-striped mb-0">
          <thead className="sticky-top bg-light">
            <tr>
              {columns.map((col) => (
                <th key={col.id} scope="col" className="small">{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              const rowIdentifier = (row[rowKey] as number | undefined) ?? rowIndex;
              return (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={rowIdentifier}>
                  {columns.map((col) => {
                    const formatted = renderProjectionCell(row, col, currency, ufValue);
                    const content = col.id === 'coverage_costs'
                      ? renderCoverageCostsCell(
                        formatted,
                        (row as Record<string, unknown>)
                          .coverage_costs_breakdown as CoverageCostsBreakdown | undefined,
                        `coverage-costs-popover-${rowIdentifier}`,
                        currency,
                        ufValue,
                      )
                      : formatted;
                    return (
                      <td key={col.id} className="small">
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
